import { Container, Text, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import React, { useEffect, useState, useRef } from "react";
import QRCode from "react-qr-code";
import { HiCheck } from "react-icons/hi";
import Lightbox, { ZoomRef } from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import { socket } from "../../services/socketService";
import { auth } from "../../utils/FirebaseUtils";

interface ZoomState {
  scale: number;
  focalXRatio: number;
  focalYRatio: number;
  containerWidth: number;
  containerHeight: number;
}

interface ReceivedFile {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
}

export default function RemoteControlled() {
  const [rcTocken, setRcTocken] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [messageHistory, setMessageHistory] = useState<string[]>([]);
  const [receivedFile, setReceivedFile] = useState<ReceivedFile | null>(null);
  const [showUI, setShowUI] = useState(false);
  const [remoteZoom, setRemoteZoom] = useState<ZoomState | null>(null);
  const [lightboxReady, setLightboxReady] = useState(false);
  const mouseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const zoomRef = useRef<ZoomRef>(null);
  const lightboxEnteredRef = useRef(false);

  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const connectedRef = useRef(false);
  connectedRef.current = connected;

  const handleMouseMove = () => {
    setShowUI(true);
    if (mouseTimeoutRef.current) clearTimeout(mouseTimeoutRef.current);
    mouseTimeoutRef.current = setTimeout(() => setShowUI(false), 3000);
  };

  useEffect(() => {
    // Generate a token
    const token = crypto.randomUUID();
    setRcTocken(token);

    const register = async () => {
      const firebaseToken = await auth.currentUser?.getIdToken();
      if (firebaseToken) {
        socket.emit("frontend-a-init", { token: firebaseToken, rcTocken: token });
      }
    };

    // Wait for Firebase auth to be ready, then register
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && !connectedRef.current) {
        register();
      }
    });

    // Listen for commands from the controller (app)
    socket.on("receive-command", (data: { command: string }) => {
      setMessageHistory((prev) => [...prev.slice(-19), data.command]);
    });

    // Listen for file sends from the controller (app)
    socket.on("receive-file", (data: { file: ReceivedFile }) => {
      setReceivedFile(data.file);
      setRemoteZoom(null);
      // Send viewport size back to the app so it can draw the correct overlay rectangle
      socket.emit("viewport-size", {
        width: window.innerWidth,
        height: window.innerHeight,
      });
    });

    // Listen for zoom updates from the app
    socket.on("apply-zoom", (data: { zoomState: ZoomState }) => {
      setRemoteZoom(data.zoomState);
    });

    socket.on("connection-ack", (data: { message: string; role: string }) => {
      if (data.role === "controlled") {
        setConnected(true);
      }
    });

    socket.on("connection-closed", () => {
      setConnected(false);
    });

    return () => {
      unsubscribe();
      socket.off("receive-command");
      socket.off("receive-file");
      socket.off("apply-zoom");
      socket.off("connection-ack");
      socket.off("connection-closed");
    };
  }, []);

  const remoteControlUrl = rcTocken
    ? `${window.location.origin}/remote-control?token=${rcTocken}`
    : "";

  // When Lightbox opens, reset UI visibility
  useEffect(() => {
    if (receivedFile) {
      setShowUI(false);
      lightboxEnteredRef.current = false;
    }
  }, [receivedFile]);

  // Apply remote zoom state via YARL Zoom plugin controller
  useEffect(() => {
    if (!remoteZoom || !lightboxEnteredRef.current || !zoomRef.current) return;

    const { scale, focalXRatio, focalYRatio } = remoteZoom;
    const zoomCtrl = zoomRef.current;

    // focalXRatio/focalYRatio: fraction of the image centered at the focal point
    // (0.5, 0.5) = center of image, (0, 0) = top-left, (1, 1) = bottom-right
    // changeZoom(dx, dy): dx/dy are pixel offsets from container center
    const containerW = window.innerWidth;
    const containerH = window.innerHeight;
    const dx = (focalXRatio - 0.5) * containerW;
    const dy = (focalYRatio - 0.5) * containerH;

    zoomCtrl.changeZoom(scale, false, dx, dy);
  }, [remoteZoom]);

  // Inject CSS to hide YARL controls when showUI is false
  useEffect(() => {
    if (!receivedFile) return;

    const styleId = 'remote-lightbox-controls';
    let styleEl = document.getElementById(styleId);

    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }

    styleEl.textContent = `
      .yarl__button_bg {
        opacity: ${showUI ? 1 : 0} !important;
        transition: opacity 0.3s;
        pointer-events: ${showUI ? 'auto' : 'none'} !important;
      }
    `;

    return () => {
      if (styleEl) styleEl.textContent = '';
    };
  }, [showUI, receivedFile]);

  return (
    <>
      {/* Fullscreen image viewer — only shown when there's a received file */}
      {!!receivedFile && (
        <div
          onMouseMove={handleMouseMove}
          style={{ position: "fixed", inset: 0, backgroundColor: "black", zIndex: 50 }}
        >
          <Lightbox
            open={true}
            slides={[{ src: receivedFile.url }]}
            close={() => { setReceivedFile(null); setRemoteZoom(null); }}
            styles={{
              root: { backgroundColor: "black" },
              container: { backgroundColor: "black" },
            }}
            carousel={{ finite: true, preload: 0 }}
            plugins={[Zoom]}
            zoom={{ ref: zoomRef }}
            on={{
              entered: () => { lightboxEnteredRef.current = true; },
            }}
          />
        </div>
      )}

      <Container
        className="w-full h-full flex flex-col"
        p={isMobile ? "md" : "xl"}
      >
        <div className="flex flex-column justify-between items-center">
          <Text fz="xl" className="title" fw={500}>
            Remote Control
          </Text>
          {connected ? (
            <div className="flex flex-row gap-2 justify-center items-center">
              <div className="bg-green-500 rounded-full w-2 h-2 animate-pulse"></div>
              <Text fz="md" c="dimmed">
                Waiting for controller...
              </Text>
            </div>
          ) : (
            <div className="flex flex-row gap-2 justify-center items-center">
              <div className="bg-red-500 rounded-full w-2 h-2"></div>
              <Text fz="md" c="dimmed">
                Connecting...
              </Text>
            </div>
          )}
        </div>

        {/* QR Code */}
        <div className="flex flex-col items-center justify-center flex-1 gap-6">
          <Text fz="lg" c="dimmed">
            Scan this QR code with the Picnube app
          </Text>
          <div className="bg-white p-4 rounded-lg">
            {rcTocken ? (
              <QRCode
                size={256}
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                value={remoteControlUrl}
                viewBox={`0 0 256 256`}
              />
            ) : (
              <div className="w-64 h-64 flex items-center justify-center">
                <Text c="dimmed">Generating...</Text>
              </div>
            )}
          </div>
          <Text fz="sm" c="dimmed" className="text-center px-4">
            Open the Picnube app, go to Remote Control, and scan this code
          </Text>
        </div>

        {/* Incoming messages */}
        {messageHistory.length > 0 && (
          <div className="mt-4 p-4 bg-grey-800 rounded-lg">
            <Text fz="sm" fw={600} mb={2}>
              Received messages:
            </Text>
            <div className="flex flex-col gap-1 max-h-40 overflow-y-auto">
              {messageHistory.map((msg, i) => (
                <div key={i} className="flex flex-row items-center gap-2">
                  <HiCheck size={14} className="text-green-500" />
                  <Text fz="sm" c="dimmed">
                    {msg}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        )}
      </Container>
    </>
  );
}
